'use client';

import cn from 'classnames';
import React, {FC, useEffect, useRef, useState} from 'react';
import {RequestLoader} from '../Loaders';
import AddButton from './AddButton';
import styles from './Button.module.css';
import {ButtonRoundingSize, ButtonTheme, roundingSizeClasses, themeClasses} from './types';

export interface ButtonSubmodules {
	Add: typeof AddButton;
}

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
	theme?: ButtonTheme;
	roundingSize?: ButtonRoundingSize;
	highlight?: boolean;
	fullWidth?: boolean;
	isLoading?: boolean;
	loadingDelay?: number;
	loaderInvert?: boolean;
};

export const Button: FC<ButtonProps> & ButtonSubmodules = ({
	className,
	theme,
	roundingSize,
	highlight,
	fullWidth,
	isLoading,
	loadingDelay = 300,
	loaderInvert,
	children,
	...buttonProps
}) => {
	const timer = useRef<NodeJS.Timeout>();
	const [content, setContent] = useState<React.ReactNode>(children);
	const themeClassName = theme && themeClasses[theme];
	const roundingSizeClassName = roundingSize && roundingSizeClasses[roundingSize];

	useEffect(() => {
		timer.current && clearTimeout(timer.current);

		if (isLoading) {
			timer.current = setTimeout(() => {
				setContent(
					<span className={styles.loader}>
						<RequestLoader invert={loaderInvert} />
					</span>,
				);
			}, loadingDelay);
		} else {
			setContent(children);
		}

		return () => {
			timer.current && clearTimeout(timer.current);
		};
	}, [children, isLoading, loadingDelay]);

	return (
		<button
			className={cn(styles.root, themeClassName, className, {
				'rounded-sm': roundingSize === 'small',
				'rounded-3xl': roundingSize === 'medium',
				'rounded-full': roundingSize === 'max',
				[styles.highlight]: highlight,
				[styles.fullWidth]: fullWidth,
				[styles.loading]: isLoading,
			})}
			{...buttonProps}
		>
			{content}
		</button>
	);
};

Button.Add = AddButton;
